import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { PcfValidationReport, ValidationDefinition } from '../types/pcfValidation.types';
import type {
  ValidationIssueNavigationContext,
  ValidationWorkspaceTab,
} from '../types/validationNavigation.types';
import { getStorage } from '../lib/utils/safeStorage';

interface FocusedSystem {
  name: string;
  elementIds: string[];
  systemNames?: string[];
}

export type { ValidationWorkspaceTab } from '../types/validationNavigation.types';
export type { ValidationDefinition } from '../types/pcfValidation.types';
export type ValidationRepairPanelTab = 'recommended' | 'surgery' | 'history';
export type ValidationRepairItemState = 'unresolved' | 'manual-edited' | 'revalidated' | 'resolved';
export type ValidationConditionLogic = 'all' | 'any';
export type ValidationConditionField =
  | 'category'
  | 'componentType'
  | 'familyName'
  | 'system'
  | 'standard'
  | 'parameterName'
  | 'parameterValue'
  | 'exportFieldValue'
  | 'classification'
  | 'severity'
  | 'status'
  | 'namePattern';
export type ValidationConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'startsWith'
  | 'matches'
  | 'isEmpty'
  | 'isNotEmpty';
export type ValidationConditionOutcomeType =
  | 'include'
  | 'exclude'
  | 'downgrade'
  | 'warningOnly'
  | 'routeAlternate'
  | 'requireAdditional';
export type ValidationConditionTargetKey =
  | 'required-export'
  | 'connectors'
  | 'mapping'
  | 'classification'
  | 'properties';

export interface ValidationConditionClause {
  id: string;
  field: ValidationConditionField;
  operator: ValidationConditionOperator;
  value: string;
  negate?: boolean;
}

export interface ValidationConditionGroup {
  id: string;
  logic: ValidationConditionLogic;
  clauses: ValidationConditionClause[];
}

export interface ValidationConditionOutcome {
  type: ValidationConditionOutcomeType;
  severity?: 'warning' | 'info';
  routeTo?: string;
  repairArea?: string;
}

export interface ValidationConditionSetDraft {
  name: string;
  description: string;
  enabled: boolean;
  validationId?: number | null;
  validationTitle?: string | null;
  targetKey: ValidationConditionTargetKey;
  defaultOutcome: 'include' | 'exclude';
  rootLogic: ValidationConditionLogic;
  groups: ValidationConditionGroup[];
  outcome: ValidationConditionOutcome;
  notes: string;
  rationale: string;
  priority: number;
  advancedMode: boolean;
}

export interface ValidationConditionSet {
  id: string;
  targetKey: ValidationConditionTargetKey;
  targetLabel: string;
  draft: ValidationConditionSetDraft;
  published: ValidationConditionSetDraft | null;
  archived: boolean;
  draftSavedAt?: string;
  publishedAt?: string;
}

export interface ValidationConditionStudioContext {
  targetKey: ValidationConditionTargetKey;
  selectedConditionSetId: string | null;
  selectedValidationId?: number | null;
  sourceFamilyId?: string | null;
}

export interface CustomValidationPointInput {
  targetKey: ValidationConditionTargetKey;
  title: string;
  description?: string;
  category?: string;
  defaultSeverity?: string;
}

export interface ValidationRepairDetailContext {
  familyId: string;
  selectedItemId: string | null;
  activeTab: ValidationRepairPanelTab;
  surgeryMode: boolean;
}

export interface ValidationRepairHistoryEntry {
  id: string;
  familyId: string;
  itemId: string | null;
  type: 'surgery' | 'revalidate';
  title: string;
  description: string;
  scope: string;
  status: 'pending' | 'success' | 'warning' | 'error';
  timestamp: string;
  beforeValue?: string;
  afterValue?: string;
}

export interface ValidationQuickFixOperation {
  fixType: string;
  parameterName: string;
  revitParameterName: string;
  value: string;
  elementIds: string[];
  validationId: number;
  beforeValue?: string;
  description?: string;
}

export interface ValidationQuickFixTransaction {
  id: string;
  familyId: string | null;
  itemId: string | null;
  title: string;
  description: string;
  createdAt: string;
  affectedCount: number;
  status: 'applied' | 'undone' | 'undo-failed';
  undoAvailable: boolean;
  snapshotId?: string | null;
  undoError?: string;
  forwardOperations: ValidationQuickFixOperation[];
  undoOperations: ValidationQuickFixOperation[];
}

interface PcfValidationStoreState {
  report: PcfValidationReport | null;
  /**
   * Opaque token naming the context the visible report was captured in. The
   * store never interprets it; it only hands it back so the Validation feature
   * can compare it against the context that is current now.
   */
  reportContextFingerprint: string | null;
  /** Set when something invalidated the visible report before a new run replaced it. */
  reportStale: boolean;
  focusedSystem: FocusedSystem | null;
  requestedWorkspaceTab: ValidationWorkspaceTab | null;
  issueNavigationContext: ValidationIssueNavigationContext | null;
  repairDetailContext: ValidationRepairDetailContext | null;
  repairHistory: ValidationRepairHistoryEntry[];
  repairItemStates: Record<string, ValidationRepairItemState>;
  quickFixTransactions: ValidationQuickFixTransaction[];
  conditionStudioContext: ValidationConditionStudioContext | null;
  conditionSets: ValidationConditionSet[];
  customValidationPoints: ValidationDefinition[];
  setReport: (report: PcfValidationReport | null, contextFingerprint?: string | null) => void;
  markReportStale: () => void;
  setFocusedSystem: (system: FocusedSystem | null) => void;
  clearFocusedSystem: () => void;
  requestWorkspaceTab: (tab: ValidationWorkspaceTab | null) => void;
  setIssueNavigationContext: (context: ValidationIssueNavigationContext | null) => void;
  clearIssueNavigationContext: () => void;
  openRepairDetail: (context: {
    familyId: string;
    selectedItemId?: string | null;
    activeTab?: ValidationRepairPanelTab;
    surgeryMode?: boolean;
  }) => void;
  closeRepairDetail: () => void;
  setRepairDetailSelectedItem: (itemId: string | null) => void;
  setRepairDetailActiveTab: (tab: ValidationRepairPanelTab) => void;
  setRepairSurgeryMode: (enabled: boolean) => void;
  addRepairHistoryEntry: (entry: ValidationRepairHistoryEntry) => void;
  setRepairItemState: (itemId: string, state: ValidationRepairItemState) => void;
  clearRepairStateForFamily: (familyId: string) => void;
  addQuickFixTransaction: (transaction: ValidationQuickFixTransaction) => void;
  markQuickFixTransactionUndone: (transactionId: string) => void;
  markQuickFixTransactionUndoFailed: (transactionId: string, error: string) => void;
  discardQuickFixTransaction: (transactionId: string) => void;
  openConditionStudio: (context: {
    targetKey: ValidationConditionTargetKey;
    selectedConditionSetId?: string | null;
    selectedValidationId?: number | null;
    sourceFamilyId?: string | null;
  }) => void;
  closeConditionStudio: () => void;
  createConditionSet: (
    targetKey: ValidationConditionTargetKey,
    options?: {
      validationId?: number | null;
      validationTitle?: string | null;
    }
  ) => string;
  createCustomValidationPoint: (input: CustomValidationPointInput) => ValidationDefinition;
  duplicateConditionSet: (conditionSetId: string) => string | null;
  archiveConditionSet: (conditionSetId: string) => void;
  selectConditionSet: (conditionSetId: string | null) => void;
  updateConditionSetDraft: (
    conditionSetId: string,
    updater: (draft: ValidationConditionSetDraft) => ValidationConditionSetDraft
  ) => void;
  saveConditionSetDraft: (conditionSetId: string) => void;
  publishConditionSet: (conditionSetId: string) => void;
  discardConditionSetDraft: (conditionSetId: string) => void;
  moveConditionSetPriority: (conditionSetId: string, direction: -1 | 1) => void;
}

export const CONDITION_TARGET_LABELS: Record<ValidationConditionTargetKey, string> = {
  'required-export': 'Required export data',
  connectors: 'Connector validation',
  mapping: 'Mapping validation',
  classification: 'Classification validation',
  properties: 'Property validation',
};

function createEmptyConditionClause(index = 0): ValidationConditionClause {
  return {
    id: `clause-${Date.now()}-${index}`,
    field: 'category',
    operator: 'equals',
    value: '',
    negate: false,
  };
}

function createDefaultConditionDraft(
  targetKey: ValidationConditionTargetKey,
  priority: number,
  options?: {
    validationId?: number | null;
    validationTitle?: string | null;
  }
): ValidationConditionSetDraft {
  const validationTitle = options?.validationTitle?.trim() || null;

  return {
    name: validationTitle || `${CONDITION_TARGET_LABELS[targetKey]} Scope`,
    description: validationTitle
      ? `Condition set for ${validationTitle}.`
      : `Condition set for ${CONDITION_TARGET_LABELS[targetKey].toLowerCase()}.`,
    enabled: true,
    validationId: options?.validationId ?? null,
    validationTitle,
    targetKey,
    defaultOutcome: 'include',
    rootLogic: 'all',
    groups: [
      {
        id: `group-${Date.now()}`,
        logic: 'all',
        clauses: [createEmptyConditionClause()],
      },
    ],
    outcome: {
      type: 'exclude',
    },
    notes: '',
    rationale: '',
    priority,
    advancedMode: false,
  };
}

function createConditionSet(
  targetKey: ValidationConditionTargetKey,
  priority: number,
  options?: {
    validationId?: number | null;
    validationTitle?: string | null;
  }
): ValidationConditionSet {
  const draft = createDefaultConditionDraft(targetKey, priority, options);
  return {
    id: `condition-set-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    targetKey,
    targetLabel: CONDITION_TARGET_LABELS[targetKey],
    draft,
    published: null,
    archived: false,
  };
}

export const CUSTOM_VALIDATION_ID_BASE = 100000;

function createCustomValidationPoint(
  input: CustomValidationPointInput,
  existing: ValidationDefinition[]
): ValidationDefinition {
  const maxExistingId = existing.reduce(
    (maxId, validation) => Math.max(maxId, validation.Id),
    CUSTOM_VALIDATION_ID_BASE - 1
  );
  const targetLabel = CONDITION_TARGET_LABELS[input.targetKey];
  const title = input.title.trim() || 'Custom validation';
  const description = input.description?.trim() || `Custom validation for ${targetLabel}.`;

  return {
    Id: maxExistingId + 1,
    Title: title,
    Description: `${targetLabel}. ${description}`,
    DefaultSeverity: input.defaultSeverity || 'Warning',
    Category: input.category || targetLabel,
    IsEnabled: true,
    CriticalityRank: 1000 + existing.length,
  };
}

function resolveConditionSetValidationId(conditionSet: ValidationConditionSet | null | undefined): number | null {
  return conditionSet?.draft.validationId ?? conditionSet?.published?.validationId ?? null;
}

export const usePcfValidationStore = create<PcfValidationStoreState>()(
  persist(
    (set, get) => ({
      report: null,
      reportContextFingerprint: null,
      reportStale: false,
      focusedSystem: null,
      requestedWorkspaceTab: null,
      issueNavigationContext: null,
      repairDetailContext: null,
      repairHistory: [],
      repairItemStates: {},
      quickFixTransactions: [],
      conditionStudioContext: null,
      customValidationPoints: [],
      conditionSets: [
        createConditionSet('required-export', 1),
        createConditionSet('connectors', 2),
      ],
      // A report that arrives without a context fingerprint cannot claim to
      // describe the current model, so it is published as stale rather than
      // silently trusted.
      setReport: (report, contextFingerprint = null) =>
        set({
          report,
          reportContextFingerprint: report ? contextFingerprint : null,
          reportStale: report ? !contextFingerprint : false,
        }),
      markReportStale: () => set({ reportStale: true }),
      setFocusedSystem: (focusedSystem) => set({ focusedSystem }),
      clearFocusedSystem: () => set({ focusedSystem: null }),
      requestWorkspaceTab: (requestedWorkspaceTab) => set({ requestedWorkspaceTab }),
      setIssueNavigationContext: (issueNavigationContext) => set({ issueNavigationContext }),
      clearIssueNavigationContext: () => set({ issueNavigationContext: null }),
      openRepairDetail: ({ familyId, selectedItemId = null, activeTab = 'recommended', surgeryMode = false }) =>
        set({
          repairDetailContext: {
            familyId,
            selectedItemId,
            activeTab,
            surgeryMode,
          },
        }),
      closeRepairDetail: () => set({ repairDetailContext: null }),
      setRepairDetailSelectedItem: (selectedItemId) =>
        set((state) => ({
          repairDetailContext: state.repairDetailContext
            ? {
                ...state.repairDetailContext,
                selectedItemId,
              }
            : null,
        })),
      setRepairDetailActiveTab: (activeTab) =>
        set((state) => ({
          repairDetailContext: state.repairDetailContext
            ? {
                ...state.repairDetailContext,
                activeTab,
              }
            : null,
        })),
      setRepairSurgeryMode: (surgeryMode) =>
        set((state) => ({
          repairDetailContext: state.repairDetailContext
            ? {
                ...state.repairDetailContext,
                surgeryMode,
              }
            : null,
        })),
      addRepairHistoryEntry: (entry) =>
        set((state) => ({
          repairHistory: [entry, ...state.repairHistory].slice(0, 50),
        })),
      setRepairItemState: (itemId, repairItemState) =>
        set((state) => ({
          repairItemStates: {
            ...state.repairItemStates,
            [itemId]: repairItemState,
          },
        })),
      addQuickFixTransaction: (transaction) =>
        set((state) => ({
          quickFixTransactions: [transaction, ...state.quickFixTransactions].slice(0, 50),
        })),
      markQuickFixTransactionUndone: (transactionId) =>
        set((state) => ({
          quickFixTransactions: state.quickFixTransactions.map((transaction) =>
            transaction.id === transactionId
              ? {
                  ...transaction,
                  status: 'undone',
                  undoError: undefined,
                }
              : transaction
          ),
        })),
      markQuickFixTransactionUndoFailed: (transactionId, error) =>
        set((state) => ({
          quickFixTransactions: state.quickFixTransactions.map((transaction) =>
            transaction.id === transactionId
              ? {
                  ...transaction,
                  status: 'undo-failed',
                  undoError: error,
                }
              : transaction
          ),
        })),
      discardQuickFixTransaction: (transactionId) =>
        set((state) => ({
          quickFixTransactions: state.quickFixTransactions.filter(
            (transaction) => transaction.id !== transactionId
          ),
        })),
      clearRepairStateForFamily: (familyId) =>
        set((state) => ({
          repairHistory: state.repairHistory.filter((entry) => entry.familyId !== familyId),
          repairItemStates: Object.fromEntries(
            Object.entries(state.repairItemStates).filter(([itemId]) => !itemId.startsWith(`${familyId}::`))
          ),
          quickFixTransactions: state.quickFixTransactions.filter(
            (transaction) => transaction.familyId !== familyId
          ),
          repairDetailContext:
            state.repairDetailContext?.familyId === familyId ? null : state.repairDetailContext,
        })),
      openConditionStudio: ({
        targetKey,
        selectedConditionSetId = null,
        selectedValidationId = null,
        sourceFamilyId = null,
      }) =>
        set({
          conditionStudioContext: {
            targetKey,
            selectedConditionSetId,
            selectedValidationId,
            sourceFamilyId,
          },
        }),
      closeConditionStudio: () => set({ conditionStudioContext: null }),
      createConditionSet: (targetKey, options) => {
        const priority = get().conditionSets.filter((setItem) => setItem.targetKey === targetKey && !setItem.archived).length + 1;
        const next = createConditionSet(targetKey, priority, options);
        set((state) => ({
          conditionSets: [...state.conditionSets, next],
          conditionStudioContext: {
            targetKey,
            selectedConditionSetId: next.id,
            selectedValidationId: next.draft.validationId ?? null,
            sourceFamilyId: state.conditionStudioContext?.sourceFamilyId ?? null,
          },
        }));
        return next.id;
      },
      createCustomValidationPoint: (input) => {
        const next = createCustomValidationPoint(input, get().customValidationPoints);
        set((state) => ({
          customValidationPoints: [...state.customValidationPoints, next],
        }));
        return next;
      },
      duplicateConditionSet: (conditionSetId) => {
        const original = get().conditionSets.find((item) => item.id === conditionSetId);
        if (!original) {
          return null;
        }

        const duplicate: ValidationConditionSet = {
          ...original,
          id: `condition-set-${Date.now()}-${Math.round(Math.random() * 1000)}`,
          draft: {
            ...original.draft,
            name: `${original.draft.name} Copy`,
          },
          published: null,
          publishedAt: undefined,
        };

        set((state) => ({
          conditionSets: [...state.conditionSets, duplicate],
          conditionStudioContext: {
            targetKey: duplicate.targetKey,
            selectedConditionSetId: duplicate.id,
            selectedValidationId: duplicate.draft.validationId ?? null,
            sourceFamilyId: state.conditionStudioContext?.sourceFamilyId ?? null,
          },
        }));
        return duplicate.id;
      },
      archiveConditionSet: (conditionSetId) =>
        set((state) => ({
          conditionSets: state.conditionSets.map((item) =>
            item.id === conditionSetId ? { ...item, archived: true } : item
          ),
          conditionStudioContext:
            state.conditionStudioContext?.selectedConditionSetId === conditionSetId
              ? {
                  ...state.conditionStudioContext,
                  selectedConditionSetId: null,
                  selectedValidationId: resolveConditionSetValidationId(
                    state.conditionSets.find((item) => item.id === conditionSetId)
                  ),
                }
              : state.conditionStudioContext,
        })),
      selectConditionSet: (selectedConditionSetId) =>
        set((state) => {
          if (!state.conditionStudioContext) {
            return { conditionStudioContext: null };
          }

          const selectedConditionSet =
            selectedConditionSetId != null
              ? state.conditionSets.find((item) => item.id === selectedConditionSetId) ?? null
              : null;

          return {
            conditionStudioContext: {
              ...state.conditionStudioContext,
              targetKey: selectedConditionSet?.targetKey ?? state.conditionStudioContext.targetKey,
              selectedConditionSetId,
              selectedValidationId:
                selectedConditionSet != null
                  ? resolveConditionSetValidationId(selectedConditionSet)
                  : state.conditionStudioContext.selectedValidationId ?? null,
            },
          };
        }),
      updateConditionSetDraft: (conditionSetId, updater) =>
        set((state) => {
          let updatedValidationId: number | null = state.conditionStudioContext?.selectedValidationId ?? null;

          const conditionSets = state.conditionSets.map((item) => {
            if (item.id !== conditionSetId) {
              return item;
            }

            const nextDraft = updater(item.draft);
            updatedValidationId = nextDraft.validationId ?? item.published?.validationId ?? null;

            return {
              ...item,
              draft: nextDraft,
              draftSavedAt: undefined,
            };
          });

          return {
            conditionSets,
            conditionStudioContext:
              state.conditionStudioContext?.selectedConditionSetId === conditionSetId
                ? {
                    ...state.conditionStudioContext,
                    selectedValidationId: updatedValidationId,
                  }
                : state.conditionStudioContext,
          };
        }),
      saveConditionSetDraft: (conditionSetId) =>
        set((state) => ({
          conditionSets: state.conditionSets.map((item) =>
            item.id === conditionSetId
              ? {
                  ...item,
                  draftSavedAt: new Date().toISOString(),
                }
              : item
          ),
        })),
      publishConditionSet: (conditionSetId) =>
        set((state) => ({
          conditionSets: state.conditionSets.map((item) =>
            item.id === conditionSetId
              ? {
                  ...item,
                  published: { ...item.draft },
                  draftSavedAt: new Date().toISOString(),
                  publishedAt: new Date().toISOString(),
                }
              : item
          ),
        })),
      discardConditionSetDraft: (conditionSetId) =>
        set((state) => ({
          conditionSets: state.conditionSets.map((item) =>
            item.id === conditionSetId && item.published
              ? {
                  ...item,
                  draft: { ...item.published },
                  draftSavedAt: new Date().toISOString(),
                }
              : item
          ),
        })),
      moveConditionSetPriority: (conditionSetId, direction) =>
        set((state) => {
          const current = state.conditionSets.find((item) => item.id === conditionSetId);
          if (!current) {
            return state;
          }

          const siblings = state.conditionSets
            .filter((item) => item.targetKey === current.targetKey && !item.archived)
            .sort((left, right) => left.draft.priority - right.draft.priority);
          const currentIndex = siblings.findIndex((item) => item.id === conditionSetId);
          const nextIndex = Math.min(Math.max(currentIndex + direction, 0), siblings.length - 1);
          if (currentIndex === -1 || nextIndex === currentIndex) {
            return state;
          }

          const reordered = [...siblings];
          const [moved] = reordered.splice(currentIndex, 1);
          reordered.splice(nextIndex, 0, moved);
          const priorityMap = new Map(reordered.map((item, index) => [item.id, index + 1]));

          return {
            ...state,
            conditionSets: state.conditionSets.map((item) =>
              priorityMap.has(item.id)
                ? {
                    ...item,
                    draft: {
                      ...item.draft,
                      priority: priorityMap.get(item.id) || item.draft.priority,
                    },
                  }
                : item
            ),
          };
        }),
    }),
    {
      name: 'pipeex-pcf-validation-store',
      storage: createJSONStorage(getStorage),
      version: 1,
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState as PcfValidationStoreState;
        }

        const {
          report: _staleReport,
          reportContextFingerprint: _staleFingerprint,
          reportStale: _staleFlag,
          ...stateWithoutReport
        } = persistedState as Partial<PcfValidationStoreState> & { report?: unknown };
        return stateWithoutReport as PcfValidationStoreState;
      },
      // Keep the large validation report in memory only. Persisting it forces a
      // synchronous JSON stringify after validation and makes later navigation
      // compete with localStorage work in WebView2.
      // Keep focused system transient to avoid stale filter context on relaunch.
      partialize: (state) => ({
        conditionSets: state.conditionSets,
        customValidationPoints: state.customValidationPoints,
      }),
    }
  )
);
